import { Bounds } from "../../../engine/space/bounds";
import { RNG } from "rot-js";
import buildMinSpanTree from "./buildMinSpanTree";
import { addLoops, compareRooms, GraphNode } from "./graphUtils";
import { pruneLeafs } from "./leafUtils";

type BSPOptions =
  | {
      largest?: boolean;
      maxDepth: number;
      minWidth: number;
      minRatio: number;
      maxRatio: number;
    }
  | { largest: true; maxDepth: number; minRatio: number; maxRatio: number };

export const DEFAULT_OPTIONS = {
  maxDepth: 8,
  minWidth: 8,
  minRatio: 0.45,
  maxRatio: 0.55,
};

export const BSPGenerate = (
  rng: typeof RNG,
  width: number,
  height: number,
  opts?: BSPOptions
) => {
  const graph = graphRooms(
    makeInitialRooms(rng, { x: 0, y: 0, w: width, h: height }, opts)
  );
  weighRooms(rng, graph);
  const mstgraph = buildMinSpanTree(graph, 0);
  addLoops(rng, 0.45, graph, mstgraph);
  pruneLeafs(rng, 1, mstgraph);
  return mstgraph;
};

export const BSPDivide = (rng: typeof RNG, room: Bounds, opts?: BSPOptions) => {
  return graphRooms(makeInitialRooms(rng, room, opts));
};

const makeInitialRooms = (
  rng: typeof RNG,
  bounds: Bounds,
  opts: BSPOptions = DEFAULT_OPTIONS
) => {
  const result: Bounds[] = [];
  const queue: [Bounds, number][] = [[bounds, 0]];

  let next: [Bounds, number] | undefined, area, depth, l, r;
  // eslint-disable-next-line no-cond-assign
  while ((next = queue.pop())) {
    [area, depth] = next;
    if (depth <= opts.maxDepth && (8 <= area.w || 8 <= area.h)) {
      [l, r] = splitArea(rng, area, opts);
      queue.push([l, depth + 1], [r, depth + 1]);
    } else {
      result.push(area);
    }
  }
  return result;
};

const splitArea = (rng: typeof RNG, bounds: Bounds, opts: BSPOptions) => {
  let l, r;
  const horizontalSplit = opts.largest
    ? bounds.h <= bounds.w
    : opts.minWidth <= bounds.w && opts.minRatio < bounds.w / bounds.h;
  if (horizontalSplit) {
    const min = Math.floor(bounds.w * opts.minRatio);
    const max = Math.ceil(bounds.w * opts.maxRatio);
    l = {
      x: bounds.x,
      y: bounds.y,
      w: rng.getUniformInt(min, max),
      h: bounds.h,
    };
    r = { x: bounds.x + l.w, y: bounds.y, w: bounds.w - l.w, h: bounds.h };
  } else {
    const min = Math.floor(bounds.h * opts.minRatio);
    const max = Math.ceil(bounds.h * opts.maxRatio);
    l = {
      x: bounds.x,
      y: bounds.y,
      w: bounds.w,
      h: rng.getUniformInt(min, max),
    };
    r = { x: bounds.x, y: bounds.y + l.h, w: bounds.w, h: bounds.h - l.h };
  }
  return [l, r] as const;
};

const graphRooms = (rooms: Bounds[]) => {
  const result: Map<number, GraphNode> = new Map();

  let iRoom, jRoom, iResult, jResult;
  for (let i = 0; i < rooms.length; i++) {
    iRoom = rooms[i];
    iResult =
      result.get(i) ||
      result.set(i, { bounds: iRoom, adj: [], weight: -1, z: 1 }).get(i);
    for (let j = i + 1; j < rooms.length; j++) {
      jRoom = rooms[j];
      if (compareRooms(iRoom, jRoom)) {
        jResult =
          result.get(j) ||
          result.set(j, { bounds: jRoom, adj: [], weight: -1, z: 1 }).get(j);
        iResult?.adj.push(j);
        jResult?.adj.push(i);
      }
    }
  }
  return result;
};

const weighRooms = (rng: typeof RNG, rooms: Map<number, GraphNode>) => {
  rooms.forEach((r) => (r.weight = rng.getUniform()));
};
